import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useMemo, useState, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { S3 } from "../../../../constants";
import FilterChips from "../../../athlete-portal/calendar/FilterChips";
import SvgIcon from "../../../../parts/SvgIcon";
import { RillContext } from "../../../../rill/RillReact/share/base";
import Modal from "../../../../parts/modal-new";
import DateInput from "../../../../parts/inputs/date-input";
import { canLoadAllOrganizationAthletesForTasks } from "../../../../utils/organization-permissions-helpers";

function parseOrganizationAthletesToOptions(allAthletesFromOrganization) {
  const options = [];
  const uniqueAthleteIds = new Set();
  (allAthletesFromOrganization || [])
    .filter(({ athleteStatus }) => !athleteStatus?.inactive)
    .forEach(({ athleteId, athleteName, athleteGroups, athleteAvatar }) => {
      if (!uniqueAthleteIds.has(athleteId)) {
        uniqueAthleteIds.add(athleteId);
        options.push({
          value: athleteId,
          label: athleteName,
          groups: athleteGroups || [],
          avatar: athleteAvatar,
        });
      }
    });
  return options;
}

function getInitialOptions(locals) {
  const { activeUser: { athletes } = {}, allAthletesFromOrganization } = locals;

  const isAllowToViewAllOrganizationsAthletes =
    canLoadAllOrganizationAthletesForTasks(locals.organizationRoleName);
  // A Team Coach never holds clients of their own — same reason the athlete list
  // defaults them straight to the org-wide roster — so there's no "my athletes" vs
  // "all organization athletes" split to offer them: their own list IS the org-wide
  // list, and the separate "All Organization Athletes" toggle would be a same-result
  // duplicate.
  const isTeamCoach = locals.organizationRoleName === "Team Coach";

  const referAsClients = Boolean(locals.activeUser?.referAsClients);

  let athletesOptions;
  if (isTeamCoach) {
    athletesOptions = parseOrganizationAthletesToOptions(
      allAthletesFromOrganization
    );
  } else {
    athletesOptions = [];
    (athletes || [])
      .filter(({ status }) => !status?.inactive)
      .forEach((athlete) => {
        athletesOptions.push({
          value: athlete._id,
          label: athlete.name,
          groups: athlete.groups || [],
          avatar: athlete.avatar,
        });
      });
  }

  const allAthletesFromOrganizationOptions =
    allAthletesFromOrganization &&
    isAllowToViewAllOrganizationsAthletes &&
    !isTeamCoach
      ? parseOrganizationAthletesToOptions(allAthletesFromOrganization)
      : [];

  return {
    athletesOptions,
    allAthletesFromOrganizationOptions,
    isAllowToViewAllOrganizationsAthletes:
      isAllowToViewAllOrganizationsAthletes && !isTeamCoach,
    referAsClients,
  };
}

function AssignSingleWorkoutClients(props) {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const context = useContext(RillContext);
  const locals = props.locals || context?.locals || {};
  const initialOptions = useMemo(() => getInitialOptions(locals), [locals]);

  const [state, setState] = useState({
    selectedAthletes: [],
    selectedDate: null,
    athletesOptions: initialOptions.athletesOptions,
    referAsClients: initialOptions.referAsClients,
    allAthletesFromOrganizationOptions:
      initialOptions.allAthletesFromOrganizationOptions,
    isAllowToViewAllOrganizationsAthletes:
      initialOptions.isAllowToViewAllOrganizationsAthletes,
    isSearching: false,
  });

  const {
    selectedAthletes,
    selectedDate,
    athletesOptions,
    isAllowToViewAllOrganizationsAthletes,
    allAthletesFromOrganizationOptions,
    allAthletes,
    allOrganizationAthletes,
    isSearching,
  } = state;

  const { activeUser: { athletesGroups } = {} } = locals;
  const referAsClients = locals.activeUser?.referAsClients;

  const getFiltersToClear = useCallback(
    (prev) => {
      const isActive =
        prev.allAthletes ||
        prev.allOrganizationAthletes ||
        (athletesGroups?.some((elem) => prev[elem.name]) ?? false);
      if (!isActive) return {};
      const filters = { allAthletes: false, allOrganizationAthletes: false };
      if (athletesGroups) {
        athletesGroups.forEach((elem) => {
          filters[elem.name] = false;
        });
      }
      return filters;
    },
    [athletesGroups]
  );

  const searchAthletes = useCallback((value) => {
    const label = value.toString().toLowerCase();
    setState((prev) => {
      const originalList =
        prev.allAthletesFromOrganizationOptions.length > 0
          ? [...prev.allAthletesFromOrganizationOptions].sort((a, b) =>
              a.label.localeCompare(b.label)
            )
          : prev.athletesOptions;

      const filteredArray = originalList.filter((item) =>
        item.label.toLowerCase().startsWith(label)
      );

      return {
        ...prev,
        athletesOptions: value ? filteredArray : originalList,
      };
    });
  }, []);

  const handleInputChange = useCallback((value, name) => {
    setState((prev) => {
      const {
        selectedAthletes: prevSelected,
        athletesOptions: prevOptions,
        allAthletesFromOrganizationOptions: prevOrgOptions,
        isAllowToViewAllOrganizationsAthletes: prevAllowOrg,
      } = prev;
      let selection = [...prevSelected];

      const currentCoachAthletes =
        prevOrgOptions.length > 0 && prevAllowOrg
          ? prevOrgOptions.filter((athlete) =>
              prevOptions.some((option) => option.value === athlete.value)
            )
          : prevOptions;

      if (name === "allAthletes" && value) {
        selection = currentCoachAthletes;
      } else if (
        (name === "allAthletes" || name === "allOrganizationAthletes") &&
        !value
      ) {
        selection = [];
      } else if (name === "allOrganizationAthletes" && value) {
        selection = prevOrgOptions;
      } else if (value) {
        selection =
          prevOrgOptions.length > 0 && prevAllowOrg
            ? [
                ...selection,
                ...prevOrgOptions.filter((elem) => elem.groups.includes(name)),
              ]
            : [
                ...selection,
                ...prev.athletesOptions.filter((elem) =>
                  elem.groups.includes(name)
                ),
              ];
      } else {
        selection = selection.filter((elem) => !elem.groups.includes(name));
      }

      return {
        ...prev,
        [name]: value,
        selectedAthletes: _.uniq(selection),
        refresh: !prev.refresh,
      };
    });
  }, []);

  const handleAthleteChange = useCallback(
    (recipient) => {
      if (recipient[0] === undefined) {
        setState((prev) => ({
          ...prev,
          selectedAthletes: [],
          ...getFiltersToClear(prev),
        }));
      } else {
        setState((prev) => ({ ...prev, selectedAthletes: recipient }));
      }
    },
    [getFiltersToClear]
  );

  const handleClearAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedAthletes: [],
      ...getFiltersToClear(prev),
    }));
  }, [getFiltersToClear]);

  const handleDateChange = useCallback((value) => {
    const parsed = moment(value, "MMM D, YYYY", true);
    setState((prev) => ({
      ...prev,
      selectedDate: parsed.isValid() ? parsed.format("YYYY-MM-DD") : null,
    }));
  }, []);

  const generateAthleteListItem = useCallback(
    (athlete, getImgUrl) => {
      const isSelected = selectedAthletes.some(
        (item) => item.value === athlete.value
      );
      const onClick = () =>
        isSelected
          ? handleAthleteChange(
              selectedAthletes.filter((ath) => ath.value !== athlete.value)
            )
          : handleAthleteChange([...selectedAthletes, athlete]);
      return (
        <li key={`${athlete.value}`} onClick={onClick}>
          <img
            src={getImgUrl(athlete)}
            alt=""
            style={
              getImgUrl(athlete) === "/img/new-icons/user-circle.svg"
                ? {
                    backgroundColor: "var(--background-color)",
                    padding: "8px",
                  }
                : {}
            }
          />
          <span className="athlete-select-label" title={athlete.label}>
            {athlete.label}
          </span>
        </li>
      );
    },
    [selectedAthletes, handleAthleteChange]
  );

  const getImgUrl = (athlete) => {
    if (selectedAthletes.find((selected) => selected.value === athlete.value)) {
      return "/img/new-icons/tick-circle.svg";
    }
    if (athlete.avatar) {
      return `${S3.PATH}${athlete.avatar.s3Path}`;
    }
    return "/img/new-icons/user-circle.svg";
  };

  const cycleId =
    locals?.params?.cycleId ||
    locals?.editCycle?._id ||
    locals?.query?.cycleId;

  const assignWorkout = useCallback(() => {
    const recipients = selectedAthletes.map((a) => a.value);
    if (recipients.length === 0 || !selectedDate || !cycleId) return;

    submit(`/actions/cycle/assign-single-workout-to-athletes/${cycleId}`, {
      method: "POST",
      body: { recipients, date: selectedDate },
    });
  }, [selectedAthletes, selectedDate, cycleId]);

  const submitDisabled = selectedAthletes.length === 0 || !selectedDate;

  return (
    <Modal
      className="send-program-modal"
      title={
        referAsClients
          ? t("singleWorkoutAssign.titleClient")
          : t("singleWorkoutAssign.titleAthlete")
      }
    >
      <div className="body">
        <div className="left">
          <div>
            <SelectGroupForDesktop
              handleInputChange={handleInputChange}
              state={state}
              allAthletesFromOrganizationOptions={
                allAthletesFromOrganizationOptions
              }
              isAllowToViewAllOrganizationsAthletes={
                isAllowToViewAllOrganizationsAthletes
              }
              athletesGroups={athletesGroups}
            />
            <SelectGroupForMobile
              handleInputChange={handleInputChange}
              state={state}
              allAthletesFromOrganizationOptions={
                allAthletesFromOrganizationOptions
              }
              isAllowToViewAllOrganizationsAthletes={
                isAllowToViewAllOrganizationsAthletes
              }
              athletesGroups={athletesGroups}
            />
            <SearchAthletesForMobile
              handleInputChange={handleInputChange}
              allAthletes={allAthletes}
              allAthletesFromOrganizationOptions={
                allAthletesFromOrganizationOptions
              }
              allOrganizationAthletes={allOrganizationAthletes}
              searchAthletes={searchAthletes}
              referAsClients={referAsClients}
            />
            <section>
              <header>
                {isSearching ? (
                  <>
                    <input
                      type="search"
                      className="mobile-hidden"
                      name="search"
                      placeholder={t("common:search")}
                      onChange={(e) => searchAthletes(e.target.value)}
                      data-expandable="true"
                    />
                    <SvgIcon
                      role="button"
                      data-button-type="text-white"
                      data-icon-only="true"
                      data-icon
                      sz="small"
                      onClickFn={() => {
                        searchAthletes("");
                        setState((prev) => ({ ...prev, isSearching: false }));
                      }}
                      src="/img/new-icons/close-search.svg"
                    />
                  </>
                ) : (
                  <p
                    className="p4"
                    onClick={() =>
                      setState((prev) => ({ ...prev, isSearching: true }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setState((prev) => ({ ...prev, isSearching: true }));
                      }
                    }}
                  >
                    <span>
                      {referAsClients
                        ? t("singleWorkoutAssign.selectInstructionClients")
                        : t("singleWorkoutAssign.selectInstructionAthletes")}
                    </span>
                  </p>
                )}
              </header>

              <ul className="athlete-select">
                {athletesOptions.length > 0 &&
                  athletesOptions.map((athlete) =>
                    generateAthleteListItem(athlete, getImgUrl)
                  )}
              </ul>
            </section>
          </div>
        </div>
        <div className="right">
          <header>
            <h3>{t("singleWorkoutAssign.chooseDate")}</h3>
          </header>
          <div className="mb-6 mt-2">
            <DateInput
              name="assignDate"
              altPosition
              placeholder={t("singleWorkoutAssign.datePlaceholder")}
              min={moment(0)}
              onChange={handleDateChange}
            />
          </div>
          <header>
            <h3>
              {referAsClients
                ? t("singleWorkoutAssign.selectedClients")
                : t("singleWorkoutAssign.selectedAthletes")}
            </h3>
            <button
              type="button"
              data-button-type="secondary"
              disabled={selectedAthletes.length === 0}
              onClick={handleClearAll}
            >
              {t("singleWorkoutAssign.clearAll")}
            </button>
          </header>
          <ul className="mb-6">
            <FilterChips
              filters={selectedAthletes.map((athlete) => ({
                text: athlete.label,
                id: athlete.value,
              }))}
              clearFilter={(id) => {
                setState((prev) => ({
                  ...prev,
                  selectedAthletes: prev.selectedAthletes.filter(
                    (athlete) => athlete.value !== id
                  ),
                }));
              }}
            />
          </ul>
        </div>
      </div>

      <footer className="justify-end!">
        <button
          type="button"
          data-button-type="text-white"
          data-action="cancel"
          onClick={() => {
            submit(`/actions/refresh?`, {
              method: "GET",
              body: {
                "modal[type]": "",
              },
            });
          }}
        >
          {t("common:cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          disabled={submitDisabled}
          onClick={assignWorkout}
        >
          {t("singleWorkoutAssign.assign")}
        </button>
      </footer>
    </Modal>
  );
}

export default AssignSingleWorkoutClients;

const SelectGroupForDesktop = ({
  handleInputChange,
  state,
  allAthletesFromOrganizationOptions,
  isAllowToViewAllOrganizationsAthletes,
  athletesGroups,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <fieldset className="select-by-group">
      <legend className="select-title">
        {t("singleWorkoutAssign.selectByGroup")}
      </legend>
      <div className="select-group-filters">
        <div
          className="select-group-filter"
          onClick={() => handleInputChange(!state.allAthletes, "allAthletes")}
        >
          <img
            src={`/img/new-icons/${
              state.allAthletes ? "checked" : "unchecked"
            }-square.svg`}
            alt=""
          />
          {state.referAsClients
            ? t("singleWorkoutAssign.allClients")
            : t("singleWorkoutAssign.allAthletes")}
        </div>
        {allAthletesFromOrganizationOptions?.length > 0 &&
          isAllowToViewAllOrganizationsAthletes && (
            <div
              className="select-group-filter"
              onClick={() =>
                handleInputChange(
                  !state.allOrganizationAthletes,
                  "allOrganizationAthletes"
                )
              }
            >
              <img
                src={`/img/new-icons/${
                  state.allOrganizationAthletes ? "checked" : "unchecked"
                }-square.svg`}
                alt=""
              />
              {state.referAsClients
                ? t("singleWorkoutAssign.allOrganizationClients")
                : t("singleWorkoutAssign.allOrganizationAthletes")}
            </div>
          )}
        {athletesGroups?.map((elem) => (
          <div
            key={elem.name}
            className="select-group-filter"
            onClick={() => handleInputChange(!state[elem.name], elem.name)}
          >
            <img
              src={`/img/new-icons/${
                state[elem.name] ? "checked" : "unchecked"
              }-square.svg`}
              alt=""
            />
            <span className="select-group-filter-label" title={elem.name}>
              {elem.name}
            </span>
          </div>
        ))}
      </div>
    </fieldset>
  );
};

const SelectGroupForMobile = ({
  handleInputChange,
  state,
  allAthletesFromOrganizationOptions,
  isAllowToViewAllOrganizationsAthletes,
  athletesGroups,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <div className="select-by-group-mobile force-only-show-mobile">
      <div
        className={
          state.allAthletes
            ? "select-group-filter-mobile active-select"
            : "select-group-filter-mobile"
        }
        onClick={() => handleInputChange(!state.allAthletes, "allAthletes")}
      >
        {state.referAsClients
          ? t("singleWorkoutAssign.allClients")
          : t("singleWorkoutAssign.allAthletes")}
      </div>
      {allAthletesFromOrganizationOptions.length > 0 &&
        isAllowToViewAllOrganizationsAthletes && (
          <div
            className={
              state.allOrganizationAthletes
                ? "select-group-filter-mobile active-select"
                : "select-group-filter-mobile"
            }
            onClick={() =>
              handleInputChange(
                !state.allOrganizationAthletes,
                "allOrganizationAthletes"
              )
            }
          >
            {state.referAsClients
              ? t("singleWorkoutAssign.allOrganizationClients")
              : t("singleWorkoutAssign.allOrganizationAthletes")}
          </div>
        )}
      {athletesGroups?.map((elem) => (
        <div
          key={elem.name}
          className={
            state[elem.name]
              ? "select-group-filter-mobile active-select"
              : "select-group-filter-mobile"
          }
          onClick={() => handleInputChange(!state[elem.name], elem.name)}
        >
          {elem.name}
        </div>
      ))}
    </div>
  );
};

const SearchAthletesForMobile = ({
  handleInputChange,
  allAthletes,
  allAthletesFromOrganizationOptions,
  allOrganizationAthletes,
  searchAthletes,
  referAsClients,
}) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const isAllAthletesFromOrganizationOptionsEmpty =
    (allAthletesFromOrganizationOptions?.length ?? 0) === 0;

  const handleClick = () => {
    if (isAllAthletesFromOrganizationOptionsEmpty) {
      handleInputChange(!allAthletes, "allAthletes");
    } else {
      handleInputChange(!allOrganizationAthletes, "allOrganizationAthletes");
    }
  };

  const athletesToSelect = isAllAthletesFromOrganizationOptionsEmpty
    ? allAthletes
    : allOrganizationAthletes;

  return (
    <div className="force-only-show-mobile" id="search-athlete-select">
      <input
        name="search"
        type="search"
        onChange={(e) => searchAthletes(e.target.value)}
        placeholder={
          referAsClients
            ? t("singleWorkoutAssign.searchPlaceholderClient")
            : t("singleWorkoutAssign.searchPlaceholderAthlete")
        }
        autoComplete="off"
      />
      <button type="button" onClick={handleClick}>
        <img
          src={
            athletesToSelect
              ? "/img/new-icons/checked-square.svg"
              : "/img/new-icons/unchecked-square.svg"
          }
          alt=""
        />
        <span>{t("singleWorkoutAssign.selectAll")}</span>
      </button>
    </div>
  );
};
